import React, { useEffect, useRef, useState, useCallback } from 'react';
import { apiInspectors } from "../api/inspectors";
import { apiInspections } from "../api/inspections";
import { Loader } from 'google-maps';
import { formatTime } from "../utilities/dateTime";
import { getRoles } from "../api/core/provider";
import { getTextColorForBackground } from "../utilities/colours";
import ReactDOMServer from 'react-dom/server';
import { config } from "../core";

const INSPECTOR_COLORS = ['#2563eb', '#16a34a', '#ea580c', '#9333ea'];
const MAX_INSPECTORS = 4;
const REFRESH_INTERVAL = 60000;
const WINDOW_SIZE = 3;
const BATCH_START_DATE = '2026-03-03';
const TROPHIES = ['🥇', '🥈', '🥉'];

function statusIcon(inspection) {
  if (['completed', 'submitted'].includes(inspection.Status)) {
    return <i className="fa fa-circle-check tw-text-green-600" title="Completed" />;
  }
  if (inspection.Status === 'no_access') {
    return <i className="fa fa-circle-xmark tw-text-red-500" title="No Access" />;
  }
  if (inspection.StartedAt && !inspection.StartedAt.startsWith("0001")) {
    return <i className="fa fa-circle-play tw-text-orange-500" title="In Progress" />;
  }
  return <i className="fa fa-circle tw-text-gray-400" title="Not Started" />;
}

function getWindowedInspections(inspections) {
  if (!inspections || inspections.length === 0) {return { items: [], hiddenBefore: 0, hiddenAfter: 0 };}
  if (inspections.length <= WINDOW_SIZE * 2 + 1) {
    return { items: inspections, hiddenBefore: 0, hiddenAfter: 0 };
  }

  let pivotIdx = -1;
  for (let i = 0; i < inspections.length; i++) {
    if (inspections[i].StartedAt && !inspections[i].StartedAt.startsWith("0001")) {
      pivotIdx = i;
    } else {
      break;
    }
  }

  if (pivotIdx === -1) {
    return {
      items: inspections.slice(0, WINDOW_SIZE * 2 + 1),
      hiddenBefore: 0,
      hiddenAfter: Math.max(0, inspections.length - (WINDOW_SIZE * 2 + 1))
    };
  }

  let start = Math.max(0, pivotIdx - WINDOW_SIZE);
  let end = Math.min(inspections.length, pivotIdx + WINDOW_SIZE + 1);

  if (end - start < WINDOW_SIZE * 2 + 1) {
    if (start === 0) {
      end = Math.min(inspections.length, WINDOW_SIZE * 2 + 1);
    } else if (end === inspections.length) {
      start = Math.max(0, inspections.length - (WINDOW_SIZE * 2 + 1));
    }
  }

  return {
    items: inspections.slice(start, end),
    hiddenBefore: start,
    hiddenAfter: inspections.length - end
  };
}

const TrackingDashboard = () => {
  const [inspectors, setInspectors] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [inspectorData, setInspectorData] = useState({});
  const [completedCounts, setCompletedCounts] = useState({ today: {}, cumulative: {} });
  const [google, setGoogle] = useState(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [infoWindow, setInfoWindow] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCounterRef = useRef(0);
  const refreshTimerRef = useRef(null);
  const boundsSetForRef = useRef('');
  const markersRef = useRef([]);

  const isManager = getRoles().includes("manager");

  const options = { version: "weekly", libraries: ["places", "geometry"] };
  const loader = new Loader(config.mapsKeyPlanner, options);

  useEffect(() => {
    const init = async () => {
      const resp = await apiInspectors.get();
      setInspectors(resp.data.result);
    };
    init();
    initMap();

    return () => {
      if (refreshTimerRef.current) {clearInterval(refreshTimerRef.current);}
    };
  }, []);

  const initMap = async () => {
    const g = await loader.load();
    setGoogle(g);
    const m = new g.maps.Map(document.getElementById('tracking-map'), {
      center: { lat: 27.5, lng: -82.5 },
      zoom: 7,
      mapId: '8041c9b566fb264'
    });
    setMap(m);
    setInfoWindow(new g.maps.InfoWindow());
  };

  const fetchCompletedCounts = useCallback(async (ids) => {
    if (ids.length === 0) {
      setCompletedCounts({ today: {}, cumulative: {} });
      return;
    }

    const todayStr = new Date().toISOString().split('T')[0];
    const [todayResp, cumulativeResp] = await Promise.all([
      apiInspectors.getCompletedCounts(ids, todayStr).catch(() => null),
      apiInspectors.getCompletedCounts(ids, BATCH_START_DATE).catch(() => null)
    ]);

    setCompletedCounts({
      today: todayResp?.data?.result || {},
      cumulative: cumulativeResp?.data?.result || {}
    });
  }, []);

  const fetchData = useCallback(async (ids, date, isRefresh) => {
    if (ids.length === 0) {
      setInspectorData({});
      return;
    }

    const counter = ++fetchCounterRef.current;
    if (!isRefresh) {setLoading(true);}

    const dateStr = date.toISOString().split('T')[0];
    const promises = ids.flatMap(id => [
      apiInspections.getRoute(id, dateStr).catch(() => null),
      apiInspectors.getLocation(id).catch(() => null)
    ]);

    const results = await Promise.all(promises);

    if (counter !== fetchCounterRef.current) {return;}

    const newData = {};
    ids.forEach((id, idx) => {
      const routeResp = results[idx * 2];
      const locationResp = results[idx * 2 + 1];
      const colorIdx = selectedIds.indexOf(id);
      newData[id] = {
        route: routeResp?.data?.result || null,
        location: locationResp?.data?.result || null,
        color: INSPECTOR_COLORS[colorIdx !== -1 ? colorIdx : idx],
        inspector: inspectors.find(i => i.id === id)
      };
    });

    setInspectorData(newData);
    setLoading(false);
  }, [inspectors, selectedIds]);

  useEffect(() => {
    fetchData(selectedIds, selectedDate, false);
    fetchCompletedCounts(selectedIds);

    if (refreshTimerRef.current) {clearInterval(refreshTimerRef.current);}
    if (selectedIds.length > 0) {
      refreshTimerRef.current = setInterval(() => {
        fetchData(selectedIds, selectedDate, true);
        fetchCompletedCounts(selectedIds);
      }, REFRESH_INTERVAL);
    }

    // Reset bounds tracking when selection changes
    boundsSetForRef.current = '';

    return () => {
      if (refreshTimerRef.current) {clearInterval(refreshTimerRef.current);}
    };
  }, [selectedIds, selectedDate]);

  useEffect(() => {
    updateMarkers();
  }, [inspectorData, google, map]);

  const updateMarkers = async () => {
    if (!google || !map) {return;}

    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
    const newMarkers = [];
    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    for (const id of Object.keys(inspectorData)) {
      const data = inspectorData[id];
      if (!data.route?.RouteInspections) {continue;}

      data.route.RouteInspections.forEach((inspection, idx) => {
        if (!inspection.Lat || !inspection.Lng) {return;}

        const marker = createMarker(AdvancedMarkerElement, inspection, idx, data.color, id);
        newMarkers.push(marker);
        bounds.extend(marker.position);
        hasPoints = true;
      });

      if (data.location && data.location.ID > 0) {
        const marker = createLocationMarker(AdvancedMarkerElement, data.location, data.color);
        newMarkers.push(marker);
        bounds.extend({ lat: data.location.Lat, lng: data.location.Long });
        hasPoints = true;
      }
    }

    // Remove old markers after creating new ones (swap, not clear-then-create)
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = newMarkers;
    setMarkers(newMarkers);

    // Only fitBounds when the selection changes, not on refresh
    const selectionKey = selectedIds.sort().join(',');
    if (hasPoints && boundsSetForRef.current !== selectionKey) {
      map.fitBounds(bounds);
      boundsSetForRef.current = selectionKey;
    }
  };

  function createMarker(AdvancedMarkerElement, inspection, idx, color, inspectorId) {
    const tag = document.createElement("div");
    tag.className = "inspection-map-tag";
    tag.textContent = idx + 1;
    tag.id = `track-${inspectorId}-${inspection.PolicyNo}`;
    tag.style.backgroundColor = color;
    tag.style.color = getTextColorForBackground(color);
    tag.style.padding = "8px 8px";
    tag.style.fontSize = "0.8rem";
    tag.style.borderRadius = "4px";
    tag.style.fontWeight = "bold";

    if (['completed', 'submitted'].includes(inspection.Status)) {
      tag.style.opacity = "0.3";
    }

    const marker = new AdvancedMarkerElement({
      map,
      position: { lat: inspection.Lat, lng: inspection.Lng },
      content: tag,
    });

    marker.addListener("click", () => {
      let content = `<div>`;
      content += `<p class="tw-text-xl"><a target="_blank" href="/inspections/${inspection.PolicyNo}/process/pre_r"><strong class="tw-text-primary-500">${inspection.PolicyNo}</strong></a></p>`;
      content += `<p>${inspection.Insured || ''}</p>`;
      content += `<p>${inspection.Address}<br/>${inspection.City}, ${inspection.State} ${inspection.Zip}</p>`;
      content += `<p>Status: <strong>${inspection.PrettyStatus || inspection.Status}</strong></p>`;
      content += `</div>`;
      infoWindow.setContent(content);
      infoWindow.open({ anchor: marker, map });
    });

    return marker;
  }

  function createLocationMarker(AdvancedMarkerElement, location, color) {
    const tag = document.createElement("div");
    const jsx =
      <div style={{
        backgroundColor: color,
        borderRadius: '50%',
        padding: '10px',
        boxShadow: '0 0 0 3px white, 0 0 12px 4px rgba(0,0,0,0.4)',
      }}>
        <svg style={{ fill: 'white', width: '28px', height: '28px', display: 'block' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
          <path d="M171.3 80L224 80l0 16 0 80 0 16L99.4 192l34.7-86.9C140.3 90 155 80 171.3 80zM272 192l0-16 0-80 0-16 81.2 0c12.2 0 23.6 5.5 31.2 15l77.6 97L272 192zm252 .6L422 65c-16.7-20.9-42-33-68.7-33L171.3 32c-36 0-68.3 21.9-81.7 55.3l-42.8 107C19.8 201.8 0 226.6 0 256L0 368c0 17.7 14.3 32 32 32l33.3 0c7.6 45.4 47.1 80 94.7 80s87.1-34.6 94.7-80l130.7 0c7.6 45.4 47.1 80 94.7 80s87.1-34.6 94.7-80l33.3 0c17.7 0 32-14.3 32-32l0-48c0-66.7-50.9-121.4-116-127.4zM434.7 368a48 48 0 1 1 90.5 32 48 48 0 1 1 -90.5-32zm135.8-16c-13.2-37.3-48.7-64-90.5-64s-77.4 26.7-90.5 64l-138.9 0c-13.2-37.3-48.7-64-90.5-64s-77.4 26.7-90.5 64L48 352l0-96c0-8.8 7.2-16 16-16l448 0c44.2 0 80 35.8 80 80l0 32-21.5 0zM160 336a48 48 0 1 1 0 96 48 48 0 1 1 0-96z" />
        </svg>
      </div>
    ;
    tag.innerHTML = ReactDOMServer.renderToString(jsx);

    return new AdvancedMarkerElement({
      map,
      position: { lat: location.Lat, lng: location.Long },
      content: tag,
    });
  }

  const toggleInspector = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      }
      if (prev.length >= MAX_INSPECTORS) {return prev;}
      return [...prev, id];
    });
  };

  const moveDate = (days) => {
    setSelectedDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + days);
      return d;
    });
  };

  function mouseOverInspection(inspectorId, policyNo) {
    const pin = document.getElementById(`track-${inspectorId}-${policyNo}`);
    if (!pin) {return;}
    pin.style.fontSize = "1.4rem";
    pin.style.padding = "0.9rem";
    pin.style.border = "2px solid black";
    pin.style.boxShadow = "0 0 15px 10px rgba(0, 0, 0, 0.3)";
    pin.style.zIndex = "1000";
  }

  function mouseOutInspection(inspectorId, policyNo) {
    const pin = document.getElementById(`track-${inspectorId}-${policyNo}`);
    if (!pin) {return;}
    pin.style.padding = "8px 8px";
    pin.style.fontSize = "0.8rem";
    pin.style.border = "none";
    pin.style.boxShadow = "none";
    pin.style.zIndex = "0";
  }

  function getRouteProgress(route) {
    const inspections = route?.RouteInspections;
    if (!inspections || inspections.length === 0) {return null;}

    const total = inspections.length;
    const completed = inspections.filter(i => ['completed', 'submitted'].includes(i.Status)).length;

    const firstEta = inspections[0]?.ArrivalTime ? new Date(inspections[0].ArrivalTime) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let firstStarted = null;
    if (inspections[0]?.StartedAt && !inspections[0].StartedAt.startsWith("0001")) {
      const started = new Date(inspections[0].StartedAt);
      if (started >= today) {
        firstStarted = started;
      }
    }

    if (!firstEta || !firstStarted) {return { completed, total, status: null, lateStartMin: 0 };}

    const offsetMs = firstStarted - firstEta;
    const lateStartMin = Math.round(offsetMs / 60000);

    const now = new Date();
    let expectedDone = 0;
    for (const ins of inspections) {
      if (!ins.ArrivalTime) {continue;}
      const adjustedEta = new Date(new Date(ins.ArrivalTime).getTime() + offsetMs);
      if (now >= adjustedEta) {
        expectedDone++;
      }
    }

    const diff = completed - expectedDone;
    let status, statusColor;
    if (diff >= 0) {
      status = "On track";
      statusColor = "#16a34a";
    } else if (diff >= -1) {
      status = "1 behind";
      statusColor = "#d97706";
    } else {
      status = `${Math.abs(diff)} behind`;
      statusColor = "#dc2626";
    }

    return { completed, total, status, statusColor, lateStartMin };
  }

  // Build leaderboard sorted by today's completed count
  const leaderboard = selectedIds
    .map(id => ({
      id,
      name: inspectorData[id]?.inspector?.name || inspectors.find(i => i.id === id)?.name || `Inspector ${id}`,
      color: INSPECTOR_COLORS[selectedIds.indexOf(id)],
      today: completedCounts.today[id] || 0,
      cumulative: completedCounts.cumulative[id] || 0,
    }))
    .sort((a, b) => b.today - a.today);

  return (
    <div className="tw-flex tw-flex-col" style={{ height: 'calc(100vh - 80px)' }}>
      <h1>Inspector Tracking</h1>

      {/* Controls */}
      <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mb-4 tw-items-center">
        <div className="tw-flex tw-gap-2 tw-items-center">
          <label>Date:
            <input className="tw-rounded-lg tw-ml-1" type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={e => setSelectedDate(e.target.valueAsDate)} />
          </label>
          <i title="Back one day" className="tw-text-lg fa fa-chevron-left tw-cursor-pointer"
            onClick={() => moveDate(-1)} />
          <i title="Forward one day" className="tw-text-lg fa fa-chevron-right tw-cursor-pointer"
            onClick={() => moveDate(1)} />
        </div>

        <div className="tw-flex tw-flex-wrap tw-gap-3 tw-items-center">
          {inspectors.map(inspector => {
            const selIdx = selectedIds.indexOf(inspector.id);
            const isSelected = selIdx !== -1;
            const color = isSelected ? INSPECTOR_COLORS[selIdx] : '#9ca3af';
            return (
              <label key={inspector.id}
                className="tw-flex tw-items-center tw-gap-1 tw-cursor-pointer tw-select-none tw-px-2 tw-py-1 tw-rounded-lg tw-border-2 tw-transition"
                style={{
                  borderColor: isSelected ? color : '#d1d5db',
                  backgroundColor: isSelected ? color + '15' : 'transparent'
                }}>
                <input type="checkbox" className="tw-hidden"
                  checked={isSelected}
                  disabled={!isSelected && selectedIds.length >= MAX_INSPECTORS}
                  onChange={() => toggleInspector(inspector.id)} />
                <span className="tw-w-3 tw-h-3 tw-rounded-full tw-inline-block tw-flex-shrink-0"
                  style={{ backgroundColor: isSelected ? color : '#d1d5db' }} />
                <span className={`tw-text-sm ${isSelected ? 'tw-font-semibold' : 'tw-text-gray-500'}`}>
                  {inspector.name}
                </span>
              </label>
            );
          })}
        </div>

        {loading && <i className="fa fa-spinner fa-spin tw-text-primary-500" />}
      </div>

      <hr className="tw-bg-secondary-200 tw-h-1 tw-w-full tw-mb-4" />

      {selectedIds.length === 0 &&
        <p className="tw-text-lg tw-text-gray-500">Select inspectors above to begin tracking</p>
      }

      {/* Leaderboard */}
      {leaderboard.length > 0 &&
        <div className="tw-flex tw-gap-3 tw-mb-4 tw-flex-wrap">
          {leaderboard.map((entry, rank) => 
            <div key={entry.id}
              className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-rounded-xl tw-border-2 tw-bg-white"
              style={{ borderColor: entry.color }}>
              <span className="tw-text-2xl">{rank < 3 ? TROPHIES[rank] : ''}</span>
              <div>
                <div className="tw-font-semibold" style={{ color: entry.color }}>{entry.name}</div>
                <div className="tw-text-sm tw-text-gray-600">
                  <span className="tw-font-bold tw-text-lg" style={{ color: entry.color }}>{entry.today}</span> today
                  <span className="tw-mx-2 tw-text-gray-300">|</span>
                  <span className="tw-font-semibold">{entry.cumulative}</span> total
                </div>
              </div>
            </div>
          )}
        </div>
      }

      {/* Main layout: map left, panels right */}
      <div className="tw-flex tw-gap-4 tw-flex-grow tw-min-h-0">
        {/* Map */}
        <div id="tracking-map"
          className="tw-shadow tw-rounded-xl tw-flex-grow"
          style={{ flex: '2 1 0%', minWidth: 0 }} />

        {/* Inspector panels */}
        {selectedIds.length > 0 &&
          <div className="tw-flex tw-flex-col tw-gap-3 tw-overflow-y-auto"
            style={{ flex: '1 1 0%', minWidth: '320px', maxWidth: '400px' }}>
            {selectedIds.map(id => {
              const data = inspectorData[id];
              if (!data) {return (
                <div key={id} className="tw-border tw-rounded-xl tw-p-4 tw-animate-pulse">
                  <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32" />
                </div>
              );}

              const inspections = data.route?.RouteInspections || [];
              const total = inspections.length;
              const completed = completedCounts.today[id] || 0;
              const progress = getRouteProgress(data.route);
              const { items, hiddenBefore, hiddenAfter } = getWindowedInspections(inspections);

              return (
                <div key={id} className="tw-border tw-rounded-xl tw-overflow-hidden tw-flex-shrink-0">
                  {/* Header */}
                  <div className="tw-px-3 tw-py-2 tw-text-white tw-font-semibold"
                    style={{ backgroundColor: data.color }}>
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <span>{data.inspector?.name || `Inspector ${id}`}</span>
                      <span className="tw-text-sm tw-font-normal tw-opacity-90">
                        {completed}/{total} done
                      </span>
                    </div>
                    {progress?.status &&
                      <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-normal tw-mt-0.5 tw-opacity-90">
                        <span className="tw-px-1.5 tw-py-0.5 tw-rounded tw-font-semibold"
                          style={{ backgroundColor: progress.statusColor, color: 'white' }}>
                          {progress.status}
                        </span>
                        {progress.lateStartMin > 5 &&
                          <span>Started {progress.lateStartMin}m late</span>
                        }
                        {progress.lateStartMin < -5 &&
                          <span>Started {Math.abs(progress.lateStartMin)}m early</span>
                        }
                      </div>
                    }
                  </div>

                  {/* Progress bar */}
                  <div className="tw-h-1.5 tw-bg-gray-200">
                    <div className="tw-h-full tw-transition-all tw-duration-500"
                      style={{
                        width: total > 0 ? `${completed / total * 100}%` : '0%',
                        backgroundColor: data.color
                      }} />
                  </div>

                  {/* Inspection list */}
                  <div className="tw-px-2 tw-py-1">
                    {total === 0 &&
                      <p className="tw-text-sm tw-text-gray-400 tw-py-2 tw-text-center">No route calculated</p>
                    }

                    {hiddenBefore > 0 &&
                      <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-0.5">
                        {hiddenBefore} earlier
                      </p>
                    }

                    {items.map(inspection => {
                      const routeIdx = inspections.indexOf(inspection);
                      const isCompleted = ['completed', 'submitted'].includes(inspection.Status);
                      const isInProgress = !isCompleted && inspection.StartedAt && !inspection.StartedAt.startsWith("0001");

                      return (
                        <div key={inspection.PolicyNo}
                          className={`tw-flex tw-items-center tw-gap-2 tw-py-1 tw-px-1 tw-rounded tw-cursor-default tw-transition hover:tw-bg-gray-100 ${isCompleted ? 'tw-opacity-60' : ''}`}
                          onMouseOver={() => mouseOverInspection(id, inspection.PolicyNo)}
                          onMouseOut={() => mouseOutInspection(id, inspection.PolicyNo)}>
                          <span className="tw-text-xs tw-text-gray-400 tw-w-5 tw-text-right tw-flex-shrink-0">
                            {routeIdx + 1}.
                          </span>
                          <span className="tw-flex-shrink-0">{statusIcon(inspection)}</span>
                          <div className="tw-flex-grow tw-min-w-0">
                            <div className="tw-flex tw-items-baseline tw-gap-1">
                              <a href={`/inspections/${inspection.PolicyNo}/process/pre_r`}
                                target="_blank" rel="noreferrer"
                                className="tw-text-sm tw-font-medium tw-text-primary-500 hover:tw-underline">
                                {inspection.PolicyNo}
                              </a>
                              <span className="tw-text-xs tw-text-gray-500 tw-truncate">
                                {inspection.Address}
                              </span>
                            </div>
                            <div className="tw-text-xs tw-text-gray-400">
                              {isCompleted && inspection.CompletedAt && !inspection.CompletedAt.startsWith("0001") &&
                                <span>Done {formatTime(new Date(inspection.CompletedAt))}</span>
                              }
                              {isInProgress &&
                                <span className="tw-text-orange-500">In progress</span>
                              }
                              {!isCompleted && !isInProgress && inspection.ArrivalTime &&
                                <span>ETA {formatTime(new Date(inspection.ArrivalTime))}</span>
                              }
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {hiddenAfter > 0 &&
                      <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-0.5">
                        {hiddenAfter} more
                      </p>
                    }
                  </div>
                </div>
              );
            })}
          </div>
        }
      </div>
    </div>
  );
};

export default TrackingDashboard;
